import { Button, Form, Input, Modal, Select, Space, Image, Row, Col, Tag } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from 'common/models/model.interface';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { IQRCodeAdmitGuestModalProps } from './eiq-secure-qr-code-admit-modal.types';
import guestService from 'common/services/guest-service/guest.service';
import { EntryType } from 'common/enums/entry-type.enum';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import AlertBlock from 'common/components/info-block/alert-block/alert-block';
import featureStore from 'common/stores/features/feature.store';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import successSound from 'assets/sounds/success.wav';
import './eiq-secure-qr-code-scanner.scss';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import { eiqSecureGuestLicenseValidation } from '../eiq-secure-guest-license/eiq-secure-guest-license.validation';
import { guestEntryTypeOptions } from '../eiq-secure-guest-license/eiq-secure-guest-license';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import TurnbackModal, { ITurnbackModalProps } from '../turnback-modal/turnback-modal';
import { Admit, TurnBack } from 'features/eiq-secure/constans/admit-actions.constants';
import { GuestType } from 'common/enums/guest-type.enum';
import { IsServiceHoursPeriodValid } from 'common/helpers/configuration.helper';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import DriveId from '../drive-id/drive-id';
import { getIdDocumentAdmitFields, saveGuestIdDocument } from 'features/eiq-secure/heplers/id-document.helper';
import { useIdDocument } from 'features/eiq-secure/hooks/use-id-document/use-id-document.hook';
import { guestTypeColorMap } from 'common/list-configs/guest-list.config';
import { getGuestTypeValue } from 'common/helpers/guest.helper';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import AdvisoryAlertModal, { IAdvisoryAlertModalProps } from '../advisory-alert-modal/advisory-alert-modal';
import { getNotesFromCheckResult } from 'features/eiq-secure/heplers/admit-notes.helper';

const openModal = (modal: ReturnType<typeof useModal>, initData: IAdvisoryAlertModalProps | ITurnbackModalProps) => {
  modal.setModalInitData(initData);
  modal.showModal();
};

const playSound = (sound: string) => {
  new Audio(sound).play();
};

const QRCodeAdmitGuestModal: FC<IModal<IQRCodeAdmitGuestModalProps, any>> = observer(({ isOpen, onClose, initData }) => {
  const [form] = Form.useForm();

  const { idDocumentRef, onIdDocumentRecognized, onUpdateFromCameraClicked } = useIdDocument({
    form,
    shouldUseDriverName: () =>
      form.getFieldValue('entryType') === EntryType.Passenger &&
      !!configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName,
    onClear: () => {
      initData!.guest.idDocument = null;
    },
  });
  const gateOptions = toGateOptions(configurationStore?.configuration?.gates || []);
  const [turnbackReason, setTurnbackReason] = useState<string>();
  const [selectedAction, setSelectedAction] = useState<string>(Admit);

  const isDrivingPrivilegesRevoked = useRef(false);
  const isAdvisoryAdmit = useRef(false);
  const admitNotes = useRef<string | null>(null);

  const resetAdmissionFlags = () => {
    isDrivingPrivilegesRevoked.current = false;
    isAdvisoryAdmit.current = false;
    admitNotes.current = null;
  };

  const onConfirmed =
    (onConfirm: (...args: any[]) => void | Promise<void>) =>
    (isSuccess?: boolean, ...args: any[]) => {
      if (!isSuccess) {
        resetAdmissionFlags();
        return;
      }
      return onConfirm(...args);
    };

  const entryTypeValue = Form.useWatch('entryType', form);

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: initData ? initData.guest : null,
  });

  useBoundPlateRecognition(() => {
    const guestPlate = initData?.guest?.carLicensePlates?.find((i: any) => i.isPrimary);
    form.setFieldValue('carLicensePlate', initData?.carLicensePlate ?? guestPlate?.number ?? '');
    form.setFieldValue(
      'carState',
      initData?.carLicenseState ?? guestPlate?.state ?? configurationStore.configuration?.default.defaultLicenseState,
    );
    form.setFieldValue('gate', initData?.gate ?? undefined);
  });

  const getFormInitData = (data?: IQRCodeAdmitGuestModalProps) => ({
    addressId: data?.addressId,
    residentId: data?.residentId,
    guestId: data?.guest?.id,
    guestRestrictions: data?.guest?.restrictions,
    gate: data?.gate,
    carLicensePlate: data?.carLicensePlate ?? plateRecognitionContext.data?.licensePlate,
    carState:
      data?.carLicenseState ?? plateRecognitionContext.data?.licenseState ?? configurationStore.configuration?.default.defaultLicenseState,
    firstName: data?.guest?.firstName,
    lastName: data?.guest?.lastName,
    companyName: data?.guest?.companyName,
  });

  useEffect(() => {
    resetAdmissionFlags();
    if (form && isOpen && initData) {
      form.setFieldsValue(getFormInitData(initData));
    }
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    resetAdmissionFlags();
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  const getIdDocumentFields = async (residentId: number, addressId: number, guestId: number) => {
    const scannedDocument = idDocumentRef.current?.getIdDocument();
    const idDocument = scannedDocument
      ? await saveGuestIdDocument(scannedDocument, residentId, addressId, guestId)
      : initData!.guest.idDocument;
    return getIdDocumentAdmitFields(idDocument);
  };

  const onFinish = async (values: any) => {
    if (!initData) {
      return;
    }

    const { residentId, addressId, isEIQPassportUsed } = initData;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, values.carLicensePlate, values.carState);

    const guestId = initData.guest.id;
    const idDocumentFields = await getIdDocumentFields(residentId, addressId, guestId);

    const admitData: any = {
      gate: values.gate,
      entryType: values.entryType,
      carState: values.carState,
      carLicensePlate: values.carLicensePlate,
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      isEIQPassportUsed,
      image: plateRecognitionContext.data?.image,
      camera: plateRecognitionContext.data?.camera,
      ...idDocumentFields,
      companyName: values.companyName || null,
      driverCompanyName: values.entryType === EntryType.Passenger ? values.driverCompanyName : null,
      driverName: values.entryType === EntryType.Passenger ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilegesRevoked.current,
      isAdvisoryAdmit: isAdvisoryAdmit.current,
      note: admitNotes.current,
    };

    const isAdmit = selectedAction === Admit;
    if (!isAdmit) {
      admitData.reason = turnbackReason;
    }

    const saveAction = isAdmit ? 'admitGuest' : 'turnbackGuest';
    await guestService[saveAction](residentId, addressId, guestId, admitData);

    notification.destroy();
    notification.success({ message: isAdmit ? 'The guest has been admitted' : 'The guest has been turned back' });
    plateRecognitionContext.clear();
    playSound(successSound);
    hideModal();
  };

  const getTitle = () => {
    if (!initData) {
      return 'Admit';
    }

    return (
      <div className="qr-code-admit-modal-title-guest">
        <div className="title">
          Approve Listed Visitor by QR code{' '}
          <Tag className="eiq-tag" color={guestTypeColorMap[initData.guest.type as GuestType]} style={{ color: '#292B2E' }}>
            {getGuestTypeValue(initData.guest.type)}
          </Tag>
        </div>
        <div className="info">
          <span>
            <span className="visiting">Visiting:</span> {initData.resident}, {initData.address}
          </span>
        </div>
      </div>
    );
  };

  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  const defaultEntryType = useMemo(() => {
    const lprContext = plateRecognitionContext.data;

    if (lprContext) {
      return EntryType.Driver;
    }

    return guestEntryTypeOptions?.find((i) => i.isApproved)?.value || guestEntryTypeOptions?.[0]?.value;
  }, []);

  const onEntryTypeChanged = async (newValue: EntryType) => {
    if (newValue === EntryType.Passenger) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', defaultLicenseState);
    }
    if (newValue === EntryType.Driver) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
    }
  };

  const submitGuestAction = (action: string, reason?: string) => {
    setSelectedAction(action);
    if (action === TurnBack) {
      setTurnbackReason(reason);
    }
    form.submit();
  };

  const continueAdmission = async () => {
    if (featureStore.isEIQAdvisoryEnabled) {
      const result = await trackPromise(
        visitorBanListService.check({
          licensePlate: form.getFieldValue('carLicensePlate'),
          firstName: form.getFieldValue('firstName'),
          lastName: form.getFieldValue('lastName'),
          companyName: form.getFieldValue('companyName'),
        }),
      );
      isAdvisoryAdmit.current = result.hasMatch || result.hasPartialMatch;
      admitNotes.current = getNotesFromCheckResult(result);

      if (isAdvisoryAdmit.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'BanList',
          isVacantAddress: false,
          isShowReason: !!configurationStore.configuration?.advisory.isDisplayReasonForSecureOfficer,
        };

        openModal(visitorBanAlertModal, modalInitData);
        return;
      }
    }

    submitGuestAction(Admit);
  };

  const handleClickAdmit = async () => {
    resetAdmissionFlags();

    if (featureStore.isEIQIntelliTicketEnabled) {
      const licensePlate = form.getFieldValue('carLicensePlate');
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      isDrivingPrivilegesRevoked.current = result.isDrivingPrivilegesRevoked;

      if (isDrivingPrivilegesRevoked.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'DrivingPrivileges',
          isVacantAddress: false,
          isShowReason: true,
        };

        openModal(drivingPrivilagesRevokeModal, modalInitData);
        return;
      }
    }

    await continueAdmission();
  };

  const drivingPrivilagesRevokeModal = useModal({
    onClose: onConfirmed((type) => {
      if (type == 'admit') {
        return continueAdmission();
      }
      if (type == 'turnback') {
        openModal(turnbackModal, { initReason: 'Driving privileges revoked' });
      }
    }),
  });

  const visitorBanAlertModal = useModal({
    onClose: onConfirmed((type) => {
      if (type === Admit) {
        submitGuestAction(Admit);
      }
      if (type === TurnBack) {
        openModal(turnbackModal, { initReason: 'On banned list' });
      }
    }),
  });

  const turnbackModal = useModal({
    onClose: onConfirmed((reason) => submitGuestAction(TurnBack, reason)),
  });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleClickAdmit();
      }
    },
    [handleClickAdmit],
  );

  const isWarningOpen = drivingPrivilagesRevokeModal.isOpenModal || visitorBanAlertModal.isOpenModal;

  useEffect(() => {
    if (turnbackModal.isOpenModal || isWarningOpen) {
      document.removeEventListener('keydown', handleKeyDown, true);
    } else {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [turnbackModal.isOpenModal, isWarningOpen, handleKeyDown]);

  const getRestrictionsMessage = () => {
    if (initData?.guest.type === GuestType.Service && !IsServiceHoursPeriodValid()) {
      let result = 'Service visitors are not allowed at these hours!';

      if (initData?.guest.restrictions) {
        result += `\n${initData.guest.restrictions}`;
      }

      return result;
    }

    return initData?.guest.restrictions;
  };

  return (
    <Modal
      centered
      width={1000}
      open={isOpen}
      wrapClassName="custom-modal custom-modal--so"
      closable={true}
      title={getTitle()}
      onCancel={hideModal}>
      <Row gutter={16}>
        {featureStore.isFastAccessAlertsEnabled && (
          <>
            {initData?.addressAlertMessage && (
              <Col xs={24} md={12}>
                <AlertBlock alertHeader="Address alert" alertMessage={initData?.addressAlertMessage} styles={{ marginBottom: '5px' }} />
              </Col>
            )}
            {initData?.residentAlertMessage && (
              <Col xs={24} md={12}>
                <AlertBlock alertHeader="Resident alert" alertMessage={initData?.residentAlertMessage} styles={{ marginBottom: '5px' }} />
              </Col>
            )}
          </>
        )}
        {((featureStore.isFastAccessRestrictionsEnabled && initData?.guest?.restrictions) ||
          (initData?.guest.type === GuestType.Service && !IsServiceHoursPeriodValid())) && (
          <Col xs={24} md={12}>
            <AlertBlock
              alertHeader="VISITOR RESTRICTIONS"
              alertMessage={getRestrictionsMessage()}
              styles={{ whiteSpace: 'pre-line', marginBottom: '5px' }}
            />
          </Col>
        )}
      </Row>
      <FormWrapper form={form} onFinish={onFinish} initialValues={getFormInitData(initData)} layout="vertical">
        <div className="container-row">
          <Row gutter={32}>
            {isInfoSectionEnabled() && (
              <Col lg={12} sm={24} className="instructions-wrapper">
                {featureStore.isDriveIdEnabled && (
                  <DriveId
                    idDocument={initData?.guest?.idDocument}
                    onUpdateFromCamera={onUpdateFromCameraClicked}
                    onImageRecognized={onIdDocumentRecognized}
                    ref={idDocumentRef}
                  />
                )}
                {featureStore.isPlateScanEnabled && image && (
                  <div className="instructions-item">
                    <span className="title">PlateScan ({camera})</span>
                    <Image src={image} alt="License Plate" loading="lazy" />
                  </div>
                )}
              </Col>
            )}
            <Col lg={isInfoSectionEnabled() ? 12 : 24} xs={24}>
              <Row gutter={{ lg: 32, md: 16, xs: 0 }}>
                <Col lg={24} xs={24}>
                  <Row gutter={21}>
                    <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                      {isServiceGuest(initData?.guest?.type) && (
                        <Form.Item name="companyName" label="Company name" rules={eiqSecureGuestLicenseValidation.carState}>
                          <Input placeholder="Enter company name" />
                        </Form.Item>
                      )}
                      <Form.Item name="firstName" label="First name">
                        <Input placeholder="Enter first name" />
                      </Form.Item>
                      <Form.Item name="lastName" label="Last name">
                        <Input placeholder="Enter last name" />
                      </Form.Item>
                      <Form.Item name="entryType" label="Entry Type" initialValue={defaultEntryType}>
                        <Select
                          options={guestEntryTypeOptions}
                          placeholder="Select Entry Type"
                          popupClassName="so-select"
                          onChange={onEntryTypeChanged}
                        />
                      </Form.Item>

                      {entryTypeValue === EntryType.Passenger && (
                        <>
                          <Form.Item name="driverFirstName" label="Driver first name">
                            <Input placeholder="Enter driver first name" />
                          </Form.Item>
                          <Form.Item name="driverLastName" label="Driver last name">
                            <Input placeholder="Enter driver last name" />
                          </Form.Item>
                          <Form.Item
                            name="driverCompanyName"
                            label="Driver company name"
                            rules={eiqSecureGuestLicenseValidation.companyName}>
                            <Input placeholder="Enter driver company name" />
                          </Form.Item>
                        </>
                      )}
                    </Col>

                    <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                      {(entryTypeValue === EntryType.Driver || entryTypeValue === EntryType.Passenger) && (
                        <>
                          <Form.Item name="carState" label="License State" rules={eiqSecureGuestLicenseValidation.carState}>
                            <SelectState placeholder="Select a license state" popupClassName="so-select" />
                          </Form.Item>
                          <Form.Item name="carLicensePlate" label="License Plate" rules={eiqSecureGuestLicenseValidation.carLicensePlate}>
                            <Input placeholder="Enter license plate" autoFocus />
                          </Form.Item>
                        </>
                      )}
                      <Form.Item name="gate" label="Location (Gate)" rules={eiqSecureGuestLicenseValidation.gate}>
                        <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Space className="footer">
          <Button className="important-btn" icon={<TurnbackIcon />} onClick={turnbackModal.showModal}>
            Turnback
          </Button>
          <Button type="primary" className="primary-btn" icon={<AdmitIcon />} onClick={handleClickAdmit}>
            Approve
          </Button>
        </Space>
      </FormWrapper>
      <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      <AdvisoryAlertModal
        isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
        onClose={drivingPrivilagesRevokeModal?.hideModal}
        initData={drivingPrivilagesRevokeModal?.initData}
      />
      <AdvisoryAlertModal
        isOpen={visitorBanAlertModal.isOpenModal}
        onClose={visitorBanAlertModal.hideModal}
        initData={visitorBanAlertModal.initData}
      />
    </Modal>
  );
});

export default React.memo(QRCodeAdmitGuestModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
